import {
  Coordinates,
  MappingQuestion,
  MappingQuestionContext,
  MappingQuestionGroup,
  MappingQuestionnaireSection,
  MappingQuestionQit,
  MappingTable,
  MappingTableCell,
  MappingTablePayload,
  QITChoices,
} from 'src/types/MappingInterface';
import {
  formMappingTableParams,
  MappingTableCellQueryParams,
  VALID_MAPPING_TABLE_QUESTION_TYPES,
} from 'src/components/ProntoHousingAdministration/QuestionMapping/Navigation/MappingTab/mappingUtils';
import axios from 'src/utils/axios';
import parseQueryParams from 'src/utils/query';
import { AxiosResponse } from 'axios';
import {
  CUSTOM_DATA_KEY,
  parseJson,
} from 'src/components/ProntoHousingAdministration/QuestionMapping/utils';
import { Core, WebViewerInstance } from '@pdftron/webviewer';
import {
  MAPPING_TABLE_ORIENTATIONS,
  MIN_COLUMN_WIDTH,
} from 'src/redux/slices/ProntoHousingAdministration/MappingTableSliceConstants';
import { GenericObject } from 'src/types/GeneralTypeProps';

export const MAPPING_TABLE_GAPS = {
  VERTICAL: 5,
  HORIZONTAL: 2,
};

export const createFilteredSectionsCopy = (
  sections: MappingQuestionnaireSection[],
  selectedGroupIds?: (number | string)[]
): MappingQuestionnaireSection[] => {
  const sectionsCopy = JSON.parse(JSON.stringify(sections));
  const newSections = [];
  sectionsCopy.forEach((section) => {
    const pages = [];

    (section.pages || []).forEach((page) => {
      const questionGroups = !selectedGroupIds
        ? page.question_groups
        : page.question_groups.filter((question_group) =>
            selectedGroupIds.includes(question_group.id)
          );

      if (questionGroups.length < 0) {
        return;
      }
      questionGroups.forEach((questionGroup) => {
        questionGroup.questions = (questionGroup.questions || []).filter(
          (question) => {
            if (question.contextId) {
              return question?.qits?.every((q) =>
                VALID_MAPPING_TABLE_QUESTION_TYPES.includes(
                  q?.qit?.question?.question_type
                )
              );
            }
            return VALID_MAPPING_TABLE_QUESTION_TYPES.includes(
              question.question_type
            );
          }
        );
      });
      const groupsWithAnswers = (questionGroups || []).filter(
        (questionGroup) => questionGroup.questions?.length > 0
      );

      if (groupsWithAnswers.length > 0) {
        // Add the skip question option to selected question groups
        if (selectedGroupIds?.length > 0) {
          groupsWithAnswers.forEach((group) => {
            group.questions.unshift({
              id: -1,
              name: 'Skip',
              qit: {
                id: -1,
                question_group: { id: group.id },
                question: {
                  name: `Skip Question Group ${group.name}`,
                },
              },
            });
          });
        }

        pages.push({
          ...page,
          question_groups: groupsWithAnswers,
        });
      }
    });

    if (pages.length > 0) {
      newSections.push({ ...section, pages });
    }
  });

  return newSections;
};

export const filterMappingTableQuestions = (
  questions: Array<MappingQuestion | MappingQuestionContext>
): Array<MappingQuestion | MappingQuestionContext> => {
  return (questions || []).filter((question) => {
    if ('contextId' in question) {
      return question?.qits?.every((q) =>
        VALID_MAPPING_TABLE_QUESTION_TYPES.includes(
          q?.qit?.question?.question_type
        )
      );
    }
    return VALID_MAPPING_TABLE_QUESTION_TYPES.includes(question.question_type);
  });
};

export const filterQuestionGroupsQuestionsBySearch = (
  questionGroup: MappingQuestionGroup,
  search: string
): Array<MappingQuestion | MappingQuestionContext> => {
  const questions = [];
  (questionGroup.questions || []).forEach((question) => {
    if ('contextId' in question) {
      const qits = (question.qits || []).filter((qit) =>
        nameMatchesSearch(search, qit.name)
      );
      if (qits.length > 0) {
        questions.push({ ...question, qits });
      }
    } else {
      if (nameMatchesSearch(search, question.name)) {
        questions.push(question);
      }
    }
  });

  return questions;
};

export const nameMatchesSearch = (search: string, name: string): boolean => {
  return name.toLowerCase().includes((search || '').toLowerCase());
};

export const getInitialExpandedItems = (
  selectedGroupIds: number[],
  sections: MappingQuestionnaireSection[]
): string[] => {
  const foundSections = [];
  const foundPages = [];
  return selectedGroupIds.reduce((acc, groupId) => {
    let page;
    const section = sections.find((section) => {
      page = section.pages.find((page) =>
        page.question_groups.some(
          (questionGroup) => questionGroup.id === groupId
        )
      );
      return page !== undefined;
    });
    if (section && !foundSections.includes(section)) {
      foundSections.push(section);
      acc.push(`section: ${section.id}`);
    }
    if (page && !foundPages.includes(page)) {
      foundPages.push(page);
      acc.push(`page: ${page.id}`);
    }
    return acc;
  }, []);
};

export const expandQuestionnaire = (
  data: MappingQuestionnaireSection[],
  { includeGroups = false, includeQuestions = false } = {}
): string[] => {
  return data.reduce((acc, section) => {
    acc.push(`section: ${section.id}`);
    section.pages.forEach((page) => {
      acc.push(`page: ${page.id}`);
      if (includeGroups) {
        page.question_groups.forEach((questionGroup) => {
          acc.push(`questionGroup: ${questionGroup.id}`);
          if (includeQuestions) {
            questionGroup.questions.forEach((question) => {
              if ('contextId' in question) {
                acc.push(`context: ${question.id}`);
              } else {
                const order = question?.appearanceOrder || question?.qitOrder;
                acc.push(
                  `question: ${
                    question?.qit?.id ?? question.id
                  }  order: ${order}`
                );
              }
            });
          }
        });
      }
    });
    return acc;
  }, []);
};

export const expandQuestionnaireCalculations = (
  data: MappingQuestionnaireSection[]
): string[] => {
  return data.reduce((acc, section) => {
    acc.push(`section: ${section.id}`);
    section.pages.forEach((page) => {
      acc.push(`page: ${page.id}`);
      page.question_groups.forEach((questionGroup) => {
        acc.push(`questionGroup: ${questionGroup.id}`);
      });
    });
    return acc;
  }, []);
};

export const createOrUpdateMappingTable = async (
  tableData: MappingTable
): Promise<AxiosResponse<MappingTable>> => {
  const { id, question_groups, ...rest } = tableData;

  const body: Partial<MappingTablePayload> = {
    ...rest,
  };

  if (question_groups) {
    body.question_groups = (question_groups || []).map((group) => group.id);
  }

  delete body.table_cells;

  if (typeof tableData.id === 'number') {
    return await axios.patch(
      `mapping_question/mapping_table/${id}/?${parseQueryParams(
        formMappingTableParams
      )}`,
      body
    );
  }

  return await axios.post(
    `mapping_question/mapping_table/?${parseQueryParams(
      formMappingTableParams
    )}`,
    body
  );
};

export const deleteMappingTableCell = async (
  tableCellId
): Promise<AxiosResponse> => {
  return await axios.delete(
    `mapping_question/mapping_table_cell/${tableCellId}/`
  );
};

export const patchMappingTableCell = async (
  tableCellId: string | number,
  tableCellBody
): Promise<AxiosResponse<MappingTableCell>> => {
  const queryParams = parseQueryParams(MappingTableCellQueryParams);
  const body = {
    ...tableCellBody,
    mapping_table:
      tableCellBody.mapping_table?.id || tableCellBody.mapping_table,
  };
  if (tableCellBody.qits) {
    body.qits = tableCellBody.qits
      .map((qit) => {
        if (qit.id) {
          return qit.id;
        }
        return qit;
      })
      .filter((qit) => qit !== -1);
  }
  if (tableCellBody.calculation) {
    body.calculation = tableCellBody.calculation.id;
  }

  body.tic_bucket_calc = null;
  return await axios.patch(
    `mapping_question/mapping_table_cell/${tableCellId}/?${queryParams}`,
    body
  );
};

export const removeGroupedAnnotation = (
  annotationManager: Core.AnnotationManager,
  annotation: Core.Annotations.Annotation,
  removeSilently = false
): void => {
  let group = annotationManager.getGroupAnnotations(annotation);
  if (removeSilently) {
    setAnnotationIgnoreChangeEvent(group, true);
  }
  annotationManager.ungroupAnnotations(group);
  group = group.filter((annot) => annot.Id !== annotation.Id);
  annotationManager.groupAnnotations(group[0], group.slice(1));
  annotationManager.deleteAnnotation(annotation);
  if (removeSilently) {
    setAnnotationIgnoreChangeEvent(group, false);
  }
};

export const addAnnotationToGroup = (
  annotationManager: Core.AnnotationManager,
  annotation: Core.Annotations.Annotation,
  primaryGroupAnnotation: Core.Annotations.Annotation,
  addSilently = false
): void => {
  const group = annotationManager.getGroupAnnotations(primaryGroupAnnotation);
  if (group.some((annot) => annot.Id === annotation.Id)) {
    return;
  }
  if (addSilently) {
    setAnnotationIgnoreChangeEvent(group, true);
    setAnnotationIgnoreChangeEvent([annotation], true);
  }
  annotationManager.addAnnotation(annotation);
  annotationManager.ungroupAnnotations(group);
  annotationManager.groupAnnotations(group[0], [...group, annotation].slice(1));

  if (addSilently) {
    setAnnotationIgnoreChangeEvent(group, false);
    setAnnotationIgnoreChangeEvent([annotation], false);
  }
};

export const setAnnotationIgnoreChangeEvent = (
  annotations: Core.Annotations.Annotation[],
  value: boolean
): void => {
  annotations.forEach((annotation) => {
    const customData = parseJson(annotation.getCustomData(CUSTOM_DATA_KEY));
    customData.ignoreChangeEvent = value;
    annotation.setCustomData(CUSTOM_DATA_KEY, JSON.stringify(customData));
  });
};

export const areArraysEqual = (firstArray, secondArray): boolean => {
  const sortedFirstArray = [...(firstArray || [])].sort();
  const sortedSecondArray = [...(secondArray || [])].sort();

  if (sortedFirstArray.length !== sortedSecondArray.length) {
    return false;
  }

  return sortedFirstArray.every(
    (value, index) => value === sortedSecondArray[index]
  );
};

export const updateMappingTableCellsTooltip = (
  annotationIds: string[],
  annotationManager: Core.AnnotationManager
): void => {
  const mappings = annotationManager
    .getAnnotationsList()
    .filter((annotation) => annotationIds.includes(annotation.Id));

  const refCustomData = parseJson(mappings[0]?.getCustomData(CUSTOM_DATA_KEY));

  const tooltipData = refCustomData.tooltipData;

  mappings.forEach((annotation, index) => {
    const customData = parseJson(annotation.getCustomData(CUSTOM_DATA_KEY));
    const text = customData.text || customData.tableCell?.text || '';
    const key = `Table Cell #${index + 1}`;
    if (tooltipData?.fields[key]) {
      tooltipData.fields[key].value = '\n' + text.split(' - ').join('\n');
    }
    customData.tooltipData = tooltipData;
  });

  mappings.forEach((annotation) => {
    const customData = parseJson(annotation.getCustomData(CUSTOM_DATA_KEY));
    customData.tooltipData = tooltipData;
    annotation.setCustomData(CUSTOM_DATA_KEY, JSON.stringify(customData));
    annotationManager.updateAnnotation(annotation);
  });
};

export const sortMappingTableCells = (a, b): number => {
  const aX = a.X ?? a.x ?? 0;
  const bX = b.X ?? b.x ?? 0;

  if (aX.toFixed(2) !== bX.toFixed(2)) {
    return aX - bX;
  }

  const aY = a.Y ?? a.y ?? 0;
  const bY = b.Y ?? b.y ?? 0;
  return aY - bY;
};

export const rearrangeMappingTableCells = (
  tableCells: MappingTableCell[],
  mapping_orientation: string,
  annotationManager: Core.AnnotationManager
): void => {
  const mappedTableCell = tableCells.find(
    (tableCell) => tableCell.annotationId
  );
  if (mappedTableCell) {
    const annotationId = mappedTableCell.annotationId;
    const annotation = annotationManager.getAnnotationById(annotationId);
    const group = annotationManager
      .getGroupAnnotations(annotation)
      .filter((annot) => annot.ToolName === 'AnnotationCreateFreeText')
      .sort(sortMappingTableCells);

    const groupedAnnotations = group.filter((annot) => annot.isGrouped());
    const mainAnnotation = group.find((annot) => !annot.isGrouped());
    const startX = mainAnnotation.X;
    const startY = mainAnnotation.Y;
    const width = mainAnnotation.Width;
    const height = mainAnnotation.Height;
    groupedAnnotations.forEach(async (annot, i) => {
      let x = startX;
      let y = startY;
      const index = i + 1;
      if (mapping_orientation === MAPPING_TABLE_ORIENTATIONS.VERTICAL) {
        x += width * index + MAPPING_TABLE_GAPS.VERTICAL * index;
      } else {
        y += height * index + MAPPING_TABLE_GAPS.HORIZONTAL * index;
      }
      annot.setX(x);
      annot.setY(y);
      annotationManager.redrawAnnotation(annot);
    });
  }
};

export const getAnnotationRect = (
  annotation: Core.Annotations.Annotation
): { x: number; y: number; width: number; height: number } => {
  return {
    x: annotation.X,
    y: annotation.Y,
    width: annotation.Width,
    height: annotation.Height,
  };
};

const createTableCellsAnnotations = (
  originalAnnotation: Core.Annotations.Annotation,
  mappingTable: MappingTable,
  tableCells: MappingTableCell[],
  core: typeof Core,
  columnsPerRow: number
): Core.Annotations.Annotation[] => {
  const { annotationManager, Annotations, documentViewer } = core;
  const { mapping_orientation, row_overflow_threshold } = mappingTable;
  const coordinates = originalAnnotation.getRect();

  const startX = coordinates.x1;
  const startY = coordinates.y1;
  const fullWidth = coordinates.x2 - coordinates.x1;
  const fullHeight = coordinates.y2 - coordinates.y1;

  const gap =
    mapping_orientation === 'vertical'
      ? MAPPING_TABLE_GAPS.VERTICAL
      : MAPPING_TABLE_GAPS.HORIZONTAL;

  const rows = Math.ceil(tableCells.length / columnsPerRow);
  const totalGapWidth = gap * (columnsPerRow - 1);
  const totalGapHeight = gap * (rows - 1);

  let widthDivisor: number;
  let heightDivisor: number;

  switch (mapping_orientation) {
    case MAPPING_TABLE_ORIENTATIONS.VERTICAL:
      widthDivisor = columnsPerRow;
      heightDivisor = row_overflow_threshold;
      break;

    case MAPPING_TABLE_ORIENTATIONS.HORIZONTAL:
      widthDivisor = row_overflow_threshold;
      heightDivisor = rows;
      break;

    case MAPPING_TABLE_ORIENTATIONS.CARD:
      widthDivisor = columnsPerRow;
      heightDivisor = rows;
      break;

    default:
      throw new Error(
        `Unsupported mapping_orientation: ${mapping_orientation}`
      );
  }

  let cellWidth = (fullWidth - totalGapWidth) / widthDivisor;
  const cellHeight = (fullHeight - totalGapHeight) / heightDivisor;

  // Enforce a minimum column width if needed
  if (cellWidth < MIN_COLUMN_WIDTH) {
    cellWidth = MIN_COLUMN_WIDTH;
  }

  const pageNumber = documentViewer.getCurrentPage();
  const annotations: Core.Annotations.Annotation[] = [];

  tableCells.forEach((tableCell, index) => {
    const colIndex = index % columnsPerRow;
    const rowIndex = Math.floor(index / columnsPerRow);

    const x = startX + colIndex * (cellWidth + gap);
    const y = startY + rowIndex * (cellHeight + gap);

    const columnSettings = {
      pageNumber,
      x,
      y,
      width: cellWidth,
      height: cellHeight,
    };

    const textAnnot = createTableCell(Annotations, tableCell, columnSettings);
    annotationManager.addAnnotation(textAnnot);
    annotations.push(textAnnot);
  });

  return annotations;
};

const createTableSizeIndicator = (
  annotations: Core.Annotations.Annotation[],
  core: typeof Core,
  mappingTable: MappingTable
): Core.Annotations.RectangleAnnotation => {
  const { Annotations } = core;

  const sizeIndicator = new Annotations.RectangleAnnotation();
  const customData: GenericObject = {
    mappingType: 'tableSizeIndicator',
    mapping_table: mappingTable.id,
    isProntoAnnotation: true,
  };
  customData.id = mappingTable.id;
  sizeIndicator.setCustomData(CUSTOM_DATA_KEY, JSON.stringify(customData));
  sizeIndicator.PageNumber = annotations[0].PageNumber;
  sizeIndicator.FillColor = new Annotations.Color(0, 0, 255, 0.35);
  sizeIndicator.StrokeColor = new Annotations.Color(0, 0, 255);
  sizeIndicator.LockedContents = true;
  sizeIndicator.NoRotate = true;

  return sizeIndicator;
};

export const getTableBoundingBox = (
  annotations: Core.Annotations.Annotation[]
): {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  tableWidth: number;
  tableHeight: number;
} => {
  if (annotations.length === 0) {
    console.error('getTableBoundingBox: annotations array is empty');
    return {
      x1: 0,
      y1: 0,
      x2: 0,
      y2: 0,
      tableWidth: 0,
      tableHeight: 0,
    };
  }

  const first = annotations[0];

  let x1 = first.getX();
  let y1 = first.getY();
  let x2 = x1 + first.getWidth();
  let y2 = y1 + first.getHeight();

  for (let i = 1; i < annotations.length; i++) {
    const ann = annotations[i];
    const xStart = ann.getX();
    const yStart = ann.getY();
    const xEnd = xStart + ann.getWidth();
    const yEnd = yStart + ann.getHeight();

    if (xStart < x1) {
      x1 = xStart;
    }
    if (yStart < y1) {
      y1 = yStart;
    }
    if (xEnd > x2) {
      x2 = xEnd;
    }
    if (yEnd > y2) {
      y2 = yEnd;
    }
  }

  return {
    x1,
    y1,
    x2,
    y2,
    tableWidth: x2 - x1,
    tableHeight: y2 - y1,
  };
};

type HandleTableOverflowIndicatorsParams = {
  mappingTableConfig?: MappingTable;
  layout_config?: number[] | { cards_config?: Coordinates[] };
  annotations?: Core.Annotations.Annotation[];
  existingOverflowIndicators?: Core.Annotations.Annotation[];
  overrides?: GenericObject;
  Core: Partial<typeof Core>;
};

export const handleTableOverflowIndicators = ({
  mappingTableConfig,
  layout_config = [],
  annotations = [],
  existingOverflowIndicators = [],
  overrides = {},
  Core,
}: HandleTableOverflowIndicatorsParams): Core.Annotations.Annotation[] => {
  let cardConfigs: Coordinates[] = [];
  if ('cards_config' in layout_config) {
    cardConfigs = layout_config.cards_config.filter((rect) => {
      return !(
        rect.x === mappingTableConfig.x && rect.y === mappingTableConfig.y
      );
    });
  }

  const { row_overflow_threshold, mapping_orientation } = mappingTableConfig;

  const { annotationManager } = Core;

  const isVertical =
    mapping_orientation === MAPPING_TABLE_ORIENTATIONS.VERTICAL;
  const isHorizontal =
    mapping_orientation === MAPPING_TABLE_ORIENTATIONS.HORIZONTAL;
  const isCard = mapping_orientation === MAPPING_TABLE_ORIENTATIONS.CARD;

  const offset = {
    x: isVertical ? 0 : 1,
    y: isVertical ? 1 : 0,
  };

  const { x1, y1, tableWidth, tableHeight } = getTableBoundingBox(annotations);

  let startX = x1;
  let startY = y1;

  const firstAnnotation = annotations[0];
  let page = firstAnnotation.PageNumber;

  if (isVertical) {
    startY += firstAnnotation.getHeight() + 1;
  } else if (isHorizontal) {
    startX += firstAnnotation.getWidth() + 1;
  }

  const indicators: Core.Annotations.Annotation[] = [];

  for (let i = 0; i < row_overflow_threshold - 1; i++) {
    let width: number;
    let height: number;

    switch (mapping_orientation) {
      case MAPPING_TABLE_ORIENTATIONS.VERTICAL:
        width = tableWidth;
        height = firstAnnotation.getHeight() - offset.y;
        break;

      case MAPPING_TABLE_ORIENTATIONS.HORIZONTAL:
        width = ((layout_config as any)[i] ?? tableWidth) - offset.x;
        height = tableHeight;
        break;

      case MAPPING_TABLE_ORIENTATIONS.CARD:
        width = tableWidth;
        height = tableHeight;
        startX = cardConfigs[i]?.x ?? startX;
        startY = cardConfigs[i]?.y ?? startY;
        page = cardConfigs[i]?.page ? cardConfigs[i]?.page + 1 : page;
        break;

      default:
        throw new Error(
          `Unsupported mapping_orientation: ${mapping_orientation}`
        );
    }

    const sizeIndicator =
      existingOverflowIndicators[i] ??
      createTableSizeIndicator(annotations, Core, mappingTableConfig);

    sizeIndicator.X = startX;
    sizeIndicator.Y = startY;
    sizeIndicator.Width = width;
    sizeIndicator.Height = height;
    sizeIndicator.PageNumber = page;
    sizeIndicator.NoRotate = true;
    sizeIndicator.NoResize =
      mapping_orientation !== MAPPING_TABLE_ORIENTATIONS.HORIZONTAL
        ? true
        : (overrides?.NoResize as boolean);
    sizeIndicator.Locked = (overrides?.Locked as boolean) || false;
    sizeIndicator.NoMove = overrides?.NoMove as boolean;

    if (!existingOverflowIndicators[i]) {
      if (mapping_orientation === MAPPING_TABLE_ORIENTATIONS.CARD) {
        (sizeIndicator as Core.Annotations.RectangleAnnotation).FillColor =
          new Core.Annotations.Color(255, 165, 0, 0.35);
        (sizeIndicator as Core.Annotations.RectangleAnnotation).StrokeColor =
          new Core.Annotations.Color(255, 165, 0);
        (sizeIndicator as Core.Annotations.RectangleAnnotation).setContents(
          `Card #${i + 1}`
        );
      }
      indicators.push(sizeIndicator);
    } else {
      annotationManager.redrawAnnotation(sizeIndicator);
    }

    if (isVertical || isCard) {
      startY += height + 1;
    } else if (isHorizontal) {
      startX += width + 1;
    }
  }

  return indicators;
};

export const createMappingTable = async (
  instance: WebViewerInstance,
  originalAnnotation: Core.Annotations.Annotation,
  tableCells: MappingTableCell[],
  mappingTable: MappingTable,
  columnsPerRow: number
): Promise<Core.Annotations.Annotation[]> => {
  const { annotationManager, documentViewer } = instance.Core;

  const annotations = createTableCellsAnnotations(
    originalAnnotation,
    mappingTable,
    tableCells,
    instance.Core,
    columnsPerRow
  );

  const overflowIndicators = handleTableOverflowIndicators({
    mappingTableConfig: mappingTable,
    layout_config: mappingTable?.layout_config || [],
    annotations,
    Core: instance.Core,
  });
  annotationManager.addAnnotations(overflowIndicators);

  let combinedAnnotations = [...annotations];
  if (mappingTable.mapping_orientation !== MAPPING_TABLE_ORIENTATIONS.CARD) {
    combinedAnnotations = [...combinedAnnotations, ...overflowIndicators];

    annotationManager.groupAnnotations(
      combinedAnnotations[0],
      combinedAnnotations.slice(1)
    );
  }

  await annotationManager.drawAnnotationsFromList([
    ...annotations,
    ...overflowIndicators,
  ]);

  documentViewer.refreshAll();
  documentViewer.updateView();

  if (mappingTable.mapping_orientation === MAPPING_TABLE_ORIENTATIONS.CARD) {
    combinedAnnotations = [...combinedAnnotations, ...overflowIndicators];
  }
  return combinedAnnotations;
};

export const createTableCell = (
  Annotations: typeof Core.Annotations,
  tableCell: MappingTableCell,
  { pageNumber, x, y, width, height }
): Core.Annotations.Annotation => {
  const annotation = new Annotations.FreeTextAnnotation();
  const customData = {
    ...tableCell,
  };
  annotation.PageNumber = pageNumber;
  annotation.X = x;
  annotation.Y = y;
  annotation.Width = width;
  annotation.Height = height;
  annotation.setContents(tableCell.text);
  annotation.setCustomData(CUSTOM_DATA_KEY, JSON.stringify(customData));
  annotation.FontSize = '10pt';
  annotation.TextAlign = 'center';
  annotation.StrokeColor = new Annotations.Color(0, 0, 0);
  annotation.FillColor = new Annotations.Color(255, 255, 255, 0.5);
  annotation.LockedContents = true;
  annotation.NoRotate = true;

  return annotation;
};

export const createMappingTableCellName = (
  qit: MappingQuestionQit,
  qit_choices: QITChoices[]
): string => {
  let name = qit.question?.name;
  const qitChoices = qit_choices
    ?.filter((choice) => choice.qit === qit.id)
    .flatMap((choice) => choice.choices);

  const choicesNames = (qit.question_choices || [])
    .filter((choice) => {
      return qitChoices?.includes(choice.id);
    })
    .map((choice) => choice.name);

  if (choicesNames.length) {
    name += ` (${choicesNames.join(', ')})`;
  }
  return name;
};
